import React, { useContext } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";

import { useHistory, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import RequestsDataGrid from "components/RequestsDataGrid";
import {
  ASSIGNEE_COL_ID,
  INTERACTIVE_COL_IDS,
} from "components/RequestsDataGrid/modules/config";
import useRequestsParams from "hooks/useRequestsParams";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import { REQUESTS_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";

import * as api from "./modules/api";

interface Props {
  lastOpenedRequestId: string | null;
  clearLastOpenedRequestId: () => void;
}

const Requests = ({ lastOpenedRequestId, clearLastOpenedRequestId }: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const user = useContext(UserContext);
  const { options, filters, setOptions, setFilters, requestParams } =
    useRequestsParams();

  const canViewRequests = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestsList,
    level: PermissionLevel.Read,
  });
  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestDetails,
    level: PermissionLevel.Read,
  });

  const requestsQueryKey: QueryKey = [REQUESTS_QUERY_KEY, requestParams];

  const requests = useInfiniteQuery(
    requestsQueryKey,
    api.getRequests,
    {
      enabled: canViewRequests,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getFetchMore: (lastGroup, allGroups) =>
        allGroups.length * PAGE_SIZE < lastGroup.total
          ? allGroups.length + 1
          : undefined,
    }
  );

  return canViewRequests ? (
    <RequestsDataGrid
      requestsQueryKey={requestsQueryKey}
      filters={filters}
      options={options}
      setFilters={setFilters}
      setOptions={setOptions}
      requests={requests}
      lastOpenedRequestId={lastOpenedRequestId}
      clearLastOpenedRequestId={clearLastOpenedRequestId}
      onCellMouseDown={(e) => {
        const colId = e.column.getColId();

        if (colId === ASSIGNEE_COL_ID) {
          e.api.ensureColumnVisible(colId);
        }
      }}
      onCellClicked={(e) => {
        const colId = e.column.getColId();

        if (canViewDetails && !INTERACTIVE_COL_IDS.includes(colId)) {
          history.push(`${match.url}/${e.data.requestNumber}?navigation=true`);
        }
      }}
    />
  ) : null;
};

export default Requests;
